<template>
  <div
    ref="galleryRoot"
    class="vf-m-mobile-photo-gallery"
    :class="{
      'is-dragging': dragging,
      'is-zooming': zoomedIndex !== null,
      'is-panning': zoomPanning,
    }"
    data-page-swipe-ignore
    tabindex="0"
    role="region"
    aria-label="Product photos and videos"
    aria-describedby="mobile-gallery-keyboard-instructions"
    @touchstart.passive="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onPointerUp"
    @touchcancel="onPointerUp"
    @mousedown="onMouseDown"
    @keydown="onGalleryKeydown"
  >
    <span
      id="mobile-gallery-keyboard-instructions"
      class="gallery-keyboard-hint"
    >
      Use Left and Right Arrow keys to change media. Press Enter or Space to
      zoom a photo, and Escape to reset zoom.
    </span>
    <div ref="track" class="gallery-track" :style="trackStyle">
      <div
        v-for="(item, index) in items"
        :key="index"
        class="gallery-item"
        :style="{ background: bgColor }"
        :aria-hidden="index === activeIndex ? undefined : 'true'"
        :inert="index !== activeIndex || undefined"
      >
        <div class="gallery-item-inner">
          <!-- Video slide. Nothing of the clip is on the network until the
               visitor asks for it: the poster is a plain image, and the
               <video> element is not even created until `activateVideo`. -->
          <div v-if="isVideoItem(item)" class="gallery-video-slide">
            <img
              v-if="index > 0 || canPreviewMedia(index)"
              :key="`preview-${warmGeneration}-${mediaRetries[index] || 0}`"
              class="gallery-img gallery-media-preview"
              :class="{
                'is-loaded': previewPaintedMediaIndices.has(index),
                'is-retiring': paintedMediaIndices.has(index),
              }"
              :src="
                canPreviewMedia(index) ? previewSrc(index, item) : deferredSrc
              "
              alt=""
              loading="eager"
              decoding="async"
              fetchpriority="low"
              aria-hidden="true"
              draggable="false"
              @load="onPreviewLoad(index, $event)"
              @error="onPreviewError(index, item)"
            />
            <img
              :key="`poster-${warmGeneration}-${
                mediaRetries[index] || 0
              }-${originalSourceIndices.has(index)}`"
              class="gallery-img gallery-video-poster"
              :class="{
                'is-loaded': paintedMediaIndices.has(index),
              }"
              :alt="item?.alt || ''"
              :loading="canLoadMedia(index) ? 'eager' : 'lazy'"
              decoding="async"
              :fetchpriority="index === priorityMediaIndex ? 'high' : 'low'"
              :sizes="posterSizes(index, item)"
              :srcset="posterSrcset(index, item)"
              :src="canLoadMedia(index) ? posterSrc(index, item) : deferredSrc"
              draggable="false"
              @load="onMediaLoad(index, $event)"
              @error="onMediaError(index, item)"
            />

            <video
              v-if="isVideoActivated(index)"
              :ref="(el) => setVideoRef(index, el as HTMLVideoElement | null)"
              class="gallery-video"
              :src="item.videoUrl"
              :poster="posterSrc(index, item)"
              muted
              playsinline
              loop
              preload="auto"
              disablepictureinpicture
              controlslist="nodownload noremoteplayback noplaybackrate"
              draggable="false"
              @playing="onVideoPlaying(index)"
              @waiting="onVideoWaiting(index)"
              @pause="onVideoPause(index)"
              @error="onVideoError(index)"
              @click="onVideoToggle($event, index)"
            >
              <!-- Muted product clip, no dialogue: empty captions track for WCAG 1.2.2 -->
              <track
                kind="captions"
                srclang="en"
                label="No dialogue"
                src="data:text/vtt,WEBVTT%0A%0A"
              />
            </video>

            <!-- Play affordance, and the pause target once running. Hidden by
                 class rather than v-if so it can cross-fade instead of
                 popping in and out under the visitor's finger. -->
            <button
              type="button"
              class="gallery-video-play"
              :class="{
                'is-hidden': isVideoPlaying(index) || isVideoBuffering(index),
              }"
              :aria-label="
                isVideoActivated(index) ? 'Play video' : 'Load and play video'
              "
              :tabindex="index === activeIndex ? 0 : -1"
              @click="onVideoToggle($event, index)"
            >
              <svg
                class="gallery-video-play__icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
              </svg>
            </button>

            <span
              v-if="isVideoBuffering(index)"
              class="gallery-video-spinner"
              role="status"
              aria-label="Loading video"
            />
          </div>

          <template v-else>
            <!-- Shared-transition fallback. It uses the exact already-decoded
                 catalogue rendition and lives inside slide one, so the gallery
                 is immediately swipeable after hand-off even while the PDP
                 rendition is still decoding underneath. -->
            <img
              v-if="
                index === 0 && transitionPreviewSrc && transitionPreviewVisible
              "
              :src="transitionPreviewSrc"
              alt=""
              class="gallery-img gallery-transition-preview is-loaded"
              :class="{ 'is-retiring': transitionPreviewRetiring }"
              aria-hidden="true"
              decoding="sync"
              draggable="false"
            />

            <img
              v-if="index > 0 || canPreviewMedia(index)"
              :key="`preview-${warmGeneration}-${mediaRetries[index] || 0}`"
              :src="
                canPreviewMedia(index) ? previewSrc(index, item) : deferredSrc
              "
              alt=""
              class="gallery-img gallery-media-preview"
              :style="mediaZoomStyle(index)"
              :class="{
                'is-loaded': previewPaintedMediaIndices.has(index),
                'is-retiring': paintedMediaIndices.has(index),
              }"
              loading="eager"
              decoding="async"
              fetchpriority="low"
              aria-hidden="true"
              draggable="false"
              @load="onPreviewLoad(index, $event)"
              @error="onPreviewError(index, item)"
            />

            <img
              :key="`photo-${warmGeneration}-${
                mediaRetries[index] || 0
              }-${originalSourceIndices.has(index)}`"
              :alt="item?.alt || ''"
              class="gallery-img"
              :class="{
                'is-loaded': paintedMediaIndices.has(index),
              }"
              :style="mediaZoomStyle(index)"
              :loading="canLoadMedia(index) ? 'eager' : 'lazy'"
              decoding="async"
              :fetchpriority="index === priorityMediaIndex ? 'high' : 'low'"
              :sizes="slotSizes(index, item)"
              :srcset="slotSrcset(index, item)"
              :src="canLoadMedia(index) ? resolveDisplaySrc(item) : deferredSrc"
              draggable="false"
              @load="onMediaLoad(index, $event)"
              @error="onMediaError(index, item)"
              @click="onImageTap($event, index, item)"
            />
          </template>
        </div>
      </div>
    </div>

    <div v-if="items.length > 1" class="gallery-counter" aria-live="polite">
      {{ activeIndex + 1 }}/{{ items.length }}
    </div>
    <p v-if="mediaError" class="gallery-load-message" role="status">
      {{ mediaError }}
    </p>
    <div
      v-else-if="
        failedMediaIndices.has(activeIndex) &&
        !previewPaintedMediaIndices.has(activeIndex)
      "
      class="gallery-load-message"
      role="status"
    >
      This photo couldn't load.
      <button type="button" @click.stop="retryMedia">Try again</button>
    </div>

    <div v-if="items.length > 1" class="gallery-pagination" aria-hidden="true">
      <span class="gallery-pagination-line">
        <span
          ref="progressEl"
          class="gallery-pagination-progress"
          :style="progressStyle"
        />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from "vue";
import { hasRenditions, productAvifSrcset, renditionUrl } from "~/utils/cdn";
import {
  buildGalleryWarmOrder,
  galleryWarmLimit,
  GALLERY_WARM_CONCURRENCY,
  type GalleryConnectionInfo,
} from "~/core/utils/galleryPreloadPolicy";

interface GalleryItem {
  type?: string;
  videoUrl?: string;
  image?: string;
  imageBig?: string;
  imageLazy?: string;
  src?: string;
  src2x?: string;
  alt?: string;
  // Video items only: the product photo to show if the CDN poster derived from
  // the clip's URL has not been generated yet (see useProductMediaGallery).
  posterFallback?: string;
}

const props = defineProps({
  items: {
    type: Array as () => GalleryItem[],
    default: () => [],
  },
  height: {
    type: String,
    default: "400px",
  },
  bgColor: {
    type: String,
    default: "#f5f5f5",
  },
  transitionPreviewSrc: {
    type: String,
    default: "",
  },
  thumbnailPreviews: {
    type: Object as () => Record<string, string>,
    default: () => ({}),
  },
});

const emit = defineEmits<{
  (e: "image-click", payload: { index: number; item: GalleryItem }): void;
  (e: "slide-change", payload: { index: number }): void;
}>();

const galleryRoot = ref<HTMLElement | null>(null);
const track = ref<HTMLElement | null>(null);
const progressEl = ref<HTMLElement | null>(null);
const activeIndex = ref(0);
const priorityMediaIndex = ref(0);
const dragging = ref(false);
const didDrag = ref(false);
const transitionPreviewVisible = ref(Boolean(props.transitionPreviewSrc));
const transitionPreviewRetiring = ref(false);
const mediaError = ref("");
let transitionPreviewTimer: ReturnType<typeof setTimeout> | null = null;

// --- Non-reactive motion state ------------------------------------------------
// `translateX` and `slideWidth` used to be refs bound straight into the template.
// That meant every animation frame of a drag/spring invalidated the component and
// re-rendered EVERY slide's vnode (image + video + inline styles) — on a 8-10 photo
// product that is the single biggest source of swipe jank on a mid-range phone.
// They are plain variables now; the transform is written straight to the DOM node
// in `paint()`, so a gesture costs one style write per frame and zero re-renders.
// Only `activeIndex` (counter, emit, warm-up window) stays reactive, and it changes
// at most once per swipe.
let translateX = 0;
let slideWidth = 1;

// --- Inline tap-to-zoom (Gucci-style) ---
// Tap magnifies the active photo in place at the tap point; while zoomed, a drag
// pans the image (instead of swiping the carousel); tapping again resets it.
const ZOOM_SCALE = 2.5; // magnification on tap (adjust to taste, e.g. 2.0-3.0)
const zoomedIndex = ref<number | null>(null);
const zoomTx = ref(0);
const zoomTy = ref(0);
const zoomPanning = ref(false);
let zoomMaxX = 0; // max pan offset that still keeps the frame covered
let zoomMaxY = 0;
let zoomStartTx = 0; // pan base captured at gesture start
let zoomStartTy = 0;
let panStartX = 0;
let panStartY = 0;

let startX = 0;
let startY = 0;
let startTranslate = 0;
let lastX = 0;
let lastTime = 0;
let velocity = 0;
let axis: "x" | "y" | null = null;
let animationFrame = 0;

// Commit thresholds decide *which* slide we land on; the spring decides *how*.
const COMMIT_DISTANCE_RATIO = 0.2; // drag >20% of a slide to advance one
const FLICK_VELOCITY = 0.3; // px/ms; a faster release advances regardless of distance

// Velocity-seeded spring (mass-spring-damper). CRITICALLY DAMPED (z = 1.0): the
// track decelerates smoothly into the next photo and stops with NO overshoot/bounce,
// matching Gucci's slow ease-out feel. Lower stiffness than before -> slower glide:
// ~90% of the travel happens in the first ~0.35s, then it eases out over the final
// stretch (visible arrival ~0.6s). Verified no overshoot even on hard flicks.
// damping ratio z = c / (2*sqrt(k*m)) = 21.9 / (2*sqrt(120)) ~= 1.0.
const SPRING_STIFFNESS = 120; // k  - lower = slower, more gradual deceleration
const SPRING_DAMPING = 21.9; // c  - critical damping (2*sqrt(k*m)): decelerate, never bounce
const SPRING_MASS = 1; // m
const SPRING_REST_DISPLACEMENT = 0.5; // px: close enough to target to stop
const SPRING_REST_VELOCITY = 40; // px/s: slow enough to stop
const SPRING_MAX_DURATION = 1200; // ms: safety cap so the loop always ends

const minTranslate = () => -Math.max(0, props.items.length - 1) * slideWidth;

// Height only. The transform is applied imperatively (see `paint`) so that moving
// the track never invalidates the render.
const trackStyle = computed(() => ({
  "--gallery-height": props.height,
}));

// Width only, for the same reason; the offset is written in `paint`.
const progressStyle = computed(() => ({
  width: `${100 / Math.max(props.items.length, 1)}%`,
}));

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max));

const progressIndex = () =>
  clamp(-translateX / slideWidth, 0, props.items.length - 1);

// Single write point for both moving layers. Called from the drag handler and
// from every spring frame; `style.transform` on an already-composited layer is
// a compositor-only change, so it never touches layout or Vue.
const paint = () => {
  const t = track.value;
  if (t) {
    t.style.transform = `translate3d(${translateX}px, 0, 0)`;
  }
  const p = progressEl.value;
  if (p) {
    p.style.transform = `translateX(${progressIndex() * 100}%)`;
  }
};

const isVideoItem = (item?: GalleryItem) =>
  item?.type === "video" &&
  typeof item.videoUrl === "string" &&
  item.videoUrl.trim().length > 0;

// --- Responsive media -------------------------------------------------------
// Neighbours warm the same responsive rendition that selection will use.
// Desktop thumbnails also lend their already-loaded 600px source to any slide,
// including distant jumps, until its sharper rendition has decoded.
const resolveImageSrc = (item?: GalleryItem) =>
  item?.image || item?.imageBig || item?.src || "";
const resolveDisplaySrc = (item?: GalleryItem) => resolveImageSrc(item);
const GALLERY_SIZES = "(max-width: 1023px) 100vw, 50vw";
const measuredSizes = ref(GALLERY_SIZES);
const deferredSrc =
  "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";

const loadedMediaIndices = reactive(new Set<number>());
const visitedMediaIndices = new Set<number>();
const previewMediaIndices = reactive(new Set<number>());
const previewPaintedMediaIndices = reactive(new Set<number>());
const paintedMediaIndices = reactive(new Set<number>());
const failedMediaIndices = reactive(new Set<number>());
const originalSourceIndices = reactive(new Set<number>());
const mediaRetries = reactive<Record<number, number>>({});
const pendingMediaIndices = new Set<number>();
const posterOverrides = reactive<Record<number, string>>({});
const previewOverrides = reactive<Record<number, string>>({});
const canLoadMedia = (index: number) => loadedMediaIndices.has(index);
const canPreviewMedia = (index: number) => previewMediaIndices.has(index);
let warmHandle: number | null = null;
let warmHandleIsIdle = false;
let warmGeneration = 0;
let heroReady = false;
let warmDirection = 1;

const slotSrcset = (index: number, item?: GalleryItem) =>
  canLoadMedia(index) && !originalSourceIndices.has(index)
    ? productAvifSrcset(resolveImageSrc(item)) || undefined
    : undefined;
const slotSizes = (index: number, item?: GalleryItem) =>
  slotSrcset(index, item) ? measuredSizes.value : undefined;
const resolvePoster = (index: number, item?: GalleryItem) =>
  posterOverrides[index] || resolveImageSrc(item);
const posterSrc = (index: number, item?: GalleryItem) =>
  resolvePoster(index, item);
const posterSrcset = (index: number, item?: GalleryItem) =>
  canLoadMedia(index) && !originalSourceIndices.has(index)
    ? productAvifSrcset(resolvePoster(index, item)) || undefined
    : undefined;
const posterSizes = (index: number, item?: GalleryItem) =>
  posterSrcset(index, item) ? measuredSizes.value : undefined;
const applyPosterFallback = (index: number, item?: GalleryItem) => {
  const fallback = item?.posterFallback;
  if (!fallback || posterOverrides[index] === fallback) return false;
  posterOverrides[index] = fallback;
  originalSourceIndices.delete(index);
  return true;
};
const previewSrc = (index: number, item?: GalleryItem) => {
  if (previewOverrides[index]) return previewOverrides[index];
  const source = isVideoItem(item)
    ? resolvePoster(index, item)
    : resolveImageSrc(item);
  return hasRenditions(source)
    ? renditionUrl(source, 600, { ratio: 1, format: "avif" }) || source
    : source;
};
const hasResponsiveRendition = (index: number) => {
  const item = props.items[index];
  // The API currently fabricates lazy_*.webp URLs without checking that the
  // files exist. The production catalogue has no such files, so imageLazy is
  // not a valid preview signal. Only warm an image when a real CDN rendition
  // can be derived from the canonical image URL.
  return hasRenditions(resolveImageSrc(item));
};

const isBusy = () =>
  dragging.value || zoomPanning.value || animationFrame !== 0;
const browserConnection = () => {
  if (typeof navigator === "undefined") return null;
  return (navigator as Navigator & { connection?: GalleryConnectionInfo })
    .connection;
};
const cancelWarmSchedule = () => {
  if (warmHandle === null || typeof window === "undefined") return;
  if (warmHandleIsIdle) (window as any).cancelIdleCallback?.(warmHandle);
  else window.clearTimeout(warmHandle);
  warmHandle = null;
};

// Abort obsolete, unfinished speculation by withdrawing its source. Already
// painted previews and explicitly visited photos remain available for backswipes.
const pruneWarmMedia = (keep: Set<number>) => {
  for (const index of pendingMediaIndices) {
    if (keep.has(index) || visitedMediaIndices.has(index)) continue;
    loadedMediaIndices.delete(index);
    pendingMediaIndices.delete(index);
  }
};
const flushWarmQueue = () => {
  warmHandle = null;
  if (!heroReady || document.hidden || !isGalleryOnScreen.value) return;
  if (isBusy()) return; // the end of the gesture schedules the next window
  const centre = priorityMediaIndex.value;
  // The selected image always gets bandwidth before speculative neighbours.
  if (
    !paintedMediaIndices.has(centre) &&
    !previewPaintedMediaIndices.has(centre) &&
    !failedMediaIndices.has(centre)
  )
    return;
  const limit = galleryWarmLimit(props.items.length, browserConnection());
  const order = buildGalleryWarmOrder(
    props.items.length,
    centre,
    limit,
    warmDirection
  );
  pruneWarmMedia(new Set(order));
  for (const index of order) {
    if (pendingMediaIndices.size >= Math.min(limit, GALLERY_WARM_CONCURRENCY))
      break;
    if (
      canLoadMedia(index) ||
      failedMediaIndices.has(index) ||
      !hasResponsiveRendition(index)
    )
      continue;
    // Warm the final element itself: selection never creates another request
    // or decode for the same rendition, even with the HTTP cache disabled.
    pendingMediaIndices.add(index);
    loadedMediaIndices.add(index);
  }
};
function scheduleWarm() {
  if (typeof window === "undefined" || warmHandle !== null) return;
  if (document.hidden || !isGalleryOnScreen.value) return;
  const ric = (window as any).requestIdleCallback;
  if (typeof ric === "function") {
    warmHandleIsIdle = true;
    warmHandle = ric(flushWarmQueue, { timeout: 400 });
  } else {
    warmHandleIsIdle = false;
    warmHandle = window.setTimeout(flushWarmQueue, 120);
  }
}
const prioritizeMedia = (index: number) => {
  const target = clamp(index, 0, Math.max(0, props.items.length - 1));
  if (target !== priorityMediaIndex.value)
    warmDirection = target > priorityMediaIndex.value ? 1 : -1;
  priorityMediaIndex.value = target;
  // Release stale speculation at the first sign of swipe intent.
  cancelWarmSchedule();
  visitedMediaIndices.add(target);
  pruneWarmMedia(new Set([target]));
  pendingMediaIndices.delete(target);
  loadedMediaIndices.add(target);
  // An existing preview stays underneath until the selected rendition decodes.
  // A cold jump starts only the requested rendition, never a second download.
};

const decodeVisibleImage = async (
  index: number,
  image: HTMLImageElement | null,
  preview = false
) => {
  if (!image || !image.complete || image.naturalWidth <= 1) return;
  const source = image.currentSrc || image.src;
  if (!source || source === deferredSrc) return;
  const generation = warmGeneration;
  try {
    if (typeof image.decode === "function") await image.decode();
  } catch {
    // Some engines reject decode after a successful load. Reveal only if the
    // same connected element still owns a complete, usable image.
  }
  if (
    generation !== warmGeneration ||
    !image.isConnected ||
    source !== (image.currentSrc || image.src) ||
    !image.complete ||
    image.naturalWidth <= 1
  )
    return;
  if (preview) {
    previewPaintedMediaIndices.add(index);
  } else {
    pendingMediaIndices.delete(index);
    paintedMediaIndices.add(index);
    failedMediaIndices.delete(index);
    if (index === 0) {
      heroReady = true;
      if (transitionPreviewVisible.value) {
        transitionPreviewRetiring.value = true;
        if (transitionPreviewTimer) clearTimeout(transitionPreviewTimer);
        transitionPreviewTimer = setTimeout(() => {
          transitionPreviewVisible.value = false;
          transitionPreviewRetiring.value = false;
          transitionPreviewTimer = null;
        }, 140);
      }
    }
  }
  scheduleWarm();
};
const onMediaLoad = (index: number, event?: Event) => {
  if (!canLoadMedia(index)) return;
  const image =
    (event?.currentTarget as HTMLImageElement | null) ||
    galleryRoot.value
      ?.querySelectorAll(".gallery-item")
      [index]?.querySelector<HTMLImageElement>(
        ".gallery-img:not(.gallery-media-preview):not(.gallery-transition-preview)"
      ) ||
    null;
  void decodeVisibleImage(index, image);
};
const onPreviewLoad = (index: number, event: Event) => {
  if (!canPreviewMedia(index)) return;
  void decodeVisibleImage(index, event.currentTarget as HTMLImageElement, true);
};
const onMediaError = (index: number, item?: GalleryItem) => {
  if (!canLoadMedia(index)) return;
  if (isVideoItem(item) && applyPosterFallback(index, item)) return;
  const source = isVideoItem(item)
    ? resolvePoster(index, item)
    : resolveImageSrc(item);
  // Missing responsive rendition: try the existing original once. Never guess
  // alternative CDN names and never loop if that original is also unavailable.
  if (hasRenditions(source) && !originalSourceIndices.has(index)) {
    originalSourceIndices.add(index);
    return;
  }
  paintedMediaIndices.delete(index);
  pendingMediaIndices.delete(index);
  failedMediaIndices.add(index);
  if (index === 0) heroReady = true;
  scheduleWarm();
};
const onPreviewError = (index: number, item?: GalleryItem) => {
  if (isVideoItem(item) && applyPosterFallback(index, item)) return;
  previewPaintedMediaIndices.delete(index);
  scheduleWarm();
};
const retryMedia = () => {
  const index = activeIndex.value;
  if (!failedMediaIndices.has(index)) return;
  failedMediaIndices.delete(index);
  originalSourceIndices.delete(index);
  mediaRetries[index] = (mediaRetries[index] || 0) + 1;
  prioritizeMedia(index);
};
const onGalleryVisibilityChange = () => {
  if (document.hidden) {
    pauseActiveVideo();
    cancelWarmSchedule();
    pruneWarmMedia(new Set());
  } else {
    retryMedia();
    scheduleWarm();
  }
};

const videoRefs = new Map<number, HTMLVideoElement>();

// --- Play on request --------------------------------------------------------
// A clip is downloaded ONLY after the visitor presses play. Until then the slide
// is a poster image plus a play button, and no <video> element exists for it at
// all — so there is no `src`, no `preload`, no metadata request, nothing on the
// network. Simply reaching the slide is not consent to spend someone's data.
//
// (Previously the clip auto-loaded and auto-played as soon as its slide settled,
// which on a phone meant a multi-megabyte download and a decode the visitor had
// not asked for, every time they swiped past it.)
//
// Once activated, a clip stays mounted and cached, so pausing/resuming and
// swiping back to it are instant.
const activatedVideos = reactive(new Set<number>());
const playingVideos = reactive(new Set<number>());
const bufferingVideos = reactive(new Set<number>());

// Pauses WE issue (swipe, offscreen, slide change) as opposed to the visitor
// pressing pause. The distinction matters for the overlay: a transient pause
// during a swipe must not flash the play button back over a clip we are about
// to resume ourselves.
const autoPausedVideos = new Set<number>();

const isVideoActivated = (index: number) => activatedVideos.has(index);
const isVideoPlaying = (index: number) => playingVideos.has(index);
const isVideoBuffering = (index: number) => bufferingVideos.has(index);

// Pause whatever video is playing. Used while swiping so video decode work doesn't
// compete with the carousel's animation frames.
const pauseActiveVideo = () => {
  videoRefs.forEach((el, idx) => {
    try {
      autoPausedVideos.add(idx);
      el.pause();
    } catch {
      // ignore
    }
  });
};

const setVideoRef = (index: number, el: HTMLVideoElement | null) => {
  if (el) {
    videoRefs.set(index, el);
  } else {
    videoRefs.delete(index);
  }
};

// Swiping to another slide only pauses — the clip keeps its position so coming
// back resumes where the visitor left off rather than restarting.
const pauseOtherVideos = (excludeIndex?: number) => {
  videoRefs.forEach((el, idx) => {
    if (excludeIndex !== undefined && idx === excludeIndex) return;
    try {
      autoPausedVideos.add(idx);
      el.pause();
    } catch {
      // ignore
    }
  });
};

// Full reset, used when the gallery switches to a different product.
const resetAllVideos = () => {
  videoRefs.forEach((el) => {
    try {
      el.pause();
      el.currentTime = 0;
    } catch {
      // ignore
    }
  });
  activatedVideos.clear();
  playingVideos.clear();
  bufferingVideos.clear();
  autoPausedVideos.clear();
};

const playVideoAtIndex = (index: number) => {
  const el = videoRefs.get(index);
  if (!el) return;
  try {
    el.muted = true;
    const p = el.play();
    if (p && typeof p.catch === "function") {
      // Rejected play (autoplay policy, decode failure): drop the spinner and
      // put the play button back so the visitor can try again.
      p.catch(() => {
        bufferingVideos.delete(index);
        playingVideos.delete(index);
      });
    }
  } catch {
    bufferingVideos.delete(index);
    playingVideos.delete(index);
  }
};

// Slide changed: pause everything else, and resume this slide's clip ONLY if
// the visitor had already started it. Reaching a video slide never starts it.
const applyVideoPlaybackForActiveIndex = () => {
  const idx = activeIndex.value;
  pauseOtherVideos(idx);
  // Never (re)start playback while the gallery is scrolled offscreen — the
  // visibility observer resumes the active video when it comes back.
  if (!isGalleryOnScreen.value) return;
  if (!activatedVideos.has(idx)) return;
  const item = props.items[idx];
  if (!item || !isVideoItem(item)) return;
  void nextTick(() => {
    playVideoAtIndex(idx);
  });
};

// --- Video element events ---------------------------------------------------
const onVideoPlaying = (index: number) => {
  autoPausedVideos.delete(index);
  bufferingVideos.delete(index);
  playingVideos.add(index);
};

const onVideoWaiting = (index: number) => {
  if (playingVideos.has(index)) return; // mid-playback stall: don't flash the spinner
  bufferingVideos.add(index);
};

const onVideoPause = (index: number) => {
  bufferingVideos.delete(index);
  // A pause we issued ourselves is transient — keep the overlay hidden.
  if (autoPausedVideos.has(index)) return;
  playingVideos.delete(index);
};

// Unplayable clip (bad URL, unsupported codec): fall all the way back to the
// poster + play button rather than leaving a dead black rectangle.
const onVideoError = (index: number) => {
  bufferingVideos.delete(index);
  playingVideos.delete(index);
  activatedVideos.delete(index);
};

// The single entry point for the play button and for tapping the clip itself.
const onVideoToggle = (event: MouseEvent, index: number) => {
  event.stopPropagation();
  // A swipe that happens to end on the button is a swipe, not a tap.
  if (didDrag.value) return;

  if (!activatedVideos.has(index)) {
    // First press: this is the moment the clip is allowed on the network.
    activatedVideos.add(index);
    bufferingVideos.add(index);
    void nextTick(() => playVideoAtIndex(index));
    return;
  }

  const el = videoRefs.get(index);
  if (!el) return;

  if (el.paused) {
    autoPausedVideos.delete(index);
    playVideoAtIndex(index);
  } else {
    autoPausedVideos.delete(index); // an explicit pause must show the overlay
    el.pause();
  }
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const rubberBand = (value: number) => {
  const min = minTranslate();
  if (value > 0) {
    return value * 0.28;
  }

  if (value < min) {
    return min + (value - min) * 0.28;
  }

  return value;
};

const syncActiveIndex = () => {
  const rawIndex = -translateX / slideWidth;
  activeIndex.value = clamp(Math.round(rawIndex), 0, props.items.length - 1);
};

const measure = () => {
  // SSR-safe: the items watcher runs with immediate:true during server render,
  // where there is no DOM/window. Fall back to a width of 1 there; the real
  // measurement happens on the client (onMounted + items watcher re-run).
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0;
  // CSS slides can have fractional widths. clientWidth rounds them and the
  // error accumulates on later slides, exposing a strip of the next photo.
  // Computed width is unaffected by the PDP's entrance/zoom transforms.
  const width =
    (track.value && parseFloat(getComputedStyle(track.value).width)) ||
    viewportWidth ||
    1;
  cancelAnimation();
  slideWidth = width;
  if (track.value) measuredSizes.value = `${Math.ceil(width)}px`;
  translateX = -activeIndex.value * slideWidth;
  paint();
};

const cancelAnimation = () => {
  if (animationFrame) {
    window.cancelAnimationFrame(animationFrame);
    animationFrame = 0;
  }
};

// Velocity-seeded spring: continues the finger's motion instead of restarting
// from rest, then decelerates smoothly to a stop with no overshoot (critically damped).
// `releaseVelocity` is the finger's exit speed in px/ms (sign encodes direction).
const animateSpringTo = (targetIndex: number, releaseVelocity = 0) => {
  cancelAnimation();

  targetIndex = clamp(targetIndex, 0, props.items.length - 1);
  activeIndex.value = targetIndex;
  const to = -targetIndex * slideWidth;

  // Accessibility: reduced-motion users get an instant jump, no spring.
  if (prefersReducedMotion()) {
    translateX = to;
    paint();
    scheduleWarm();
    void nextTick(() => applyVideoPlaybackForActiveIndex());
    return;
  }

  let x = translateX;
  let v = releaseVelocity * 1000; // px/ms -> px/s, carried into the spring
  let last = window.performance.now();
  const startTime = last;

  const step = (now: number) => {
    // Fixed sub-steps keep the integrator stable across variable frame rates
    // and tab refocus (where one frame could otherwise be hundreds of ms).
    let frame = (now - last) / 1000;
    last = now;
    if (frame > 0.064) frame = 0.064;

    const SUBSTEP = 1 / 240;
    for (let remaining = frame; remaining > 0; remaining -= SUBSTEP) {
      const dt = Math.min(SUBSTEP, remaining);
      const springForce = -SPRING_STIFFNESS * (x - to);
      const dampingForce = -SPRING_DAMPING * v;
      const acceleration = (springForce + dampingForce) / SPRING_MASS;
      v += acceleration * dt;
      x += v * dt;
    }

    translateX = x;
    paint();

    const settled =
      Math.abs(x - to) < SPRING_REST_DISPLACEMENT &&
      Math.abs(v) < SPRING_REST_VELOCITY;

    if (!settled && now - startTime < SPRING_MAX_DURATION) {
      animationFrame = window.requestAnimationFrame(step);
      return;
    }

    translateX = to;
    paint();
    animationFrame = 0;
    // Gesture is over: safe to fetch/decode the next slides now.
    scheduleWarm();
    void nextTick(() => {
      applyVideoPlaybackForActiveIndex();
    });
  };

  animationFrame = window.requestAnimationFrame(step);
};

// Decide the destination slide, then hand off to the spring. One gesture moves
// at most one slide: either you dragged past the threshold, or you flicked fast.
const settle = () => {
  const originIndex = clamp(
    Math.round(-startTranslate / slideWidth),
    0,
    props.items.length - 1
  );
  const movedFromStart = translateX - startTranslate;
  const speed = Math.abs(velocity);

  // Gesture direction: left drag -> next slide (+1), right drag -> prev (-1).
  const direction =
    movedFromStart < 0
      ? 1
      : movedFromStart > 0
      ? -1
      : velocity < 0
      ? 1
      : velocity > 0
      ? -1
      : 0;

  const draggedFarEnough =
    Math.abs(movedFromStart) > COMMIT_DISTANCE_RATIO * slideWidth;
  const flicked = speed > FLICK_VELOCITY;

  const targetIndex =
    direction !== 0 && (draggedFarEnough || flicked)
      ? originIndex + direction
      : originIndex;

  animateSpringTo(targetIndex, velocity);
  prioritizeMedia(targetIndex);
};

const beginDrag = (x: number, y: number) => {
  // While zoomed, the same gesture pans the image instead of swiping slides.
  if (zoomedIndex.value !== null) {
    beginPan(x, y);
    return;
  }
  cancelAnimation();
  mediaError.value = "";
  dragging.value = true;
  didDrag.value = false;
  axis = null;
  startX = x;
  startY = y;
  lastX = x;
  lastTime = window.performance.now();
  startTranslate = translateX;
  velocity = 0;
};

const moveDrag = (x: number, y: number, event?: TouchEvent) => {
  if (zoomedIndex.value !== null) {
    movePan(x, y, event);
    return;
  }
  if (!dragging.value) return;

  const dx = x - startX;
  const dy = y - startY;

  if (!axis && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
    axis = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";
  }

  if (axis === "y") return;
  if (!axis) return;

  event?.preventDefault();
  // First real movement of a swipe: pause video so its decode/network work doesn't
  // stutter the gesture. The active clip resumes once the carousel settles.
  if (!didDrag.value) {
    pauseActiveVideo();
    const origin = clamp(
      Math.round(-startTranslate / slideWidth),
      0,
      props.items.length - 1
    );
    const next = clamp(origin + (dx < 0 ? 1 : -1), 0, props.items.length - 1);
    if (next !== origin) prioritizeMedia(next);
  }
  didDrag.value = true;

  const now = window.performance.now();
  const dt = Math.max(now - lastTime, 1);
  velocity = (x - lastX) / dt;
  lastX = x;
  lastTime = now;

  translateX = rubberBand(startTranslate + dx);
  paint();
  syncActiveIndex();
};

const onPointerUp = () => {
  // End a zoom-pan gesture (no carousel settle while zoomed).
  if (zoomPanning.value) {
    zoomPanning.value = false;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onPointerUp);
    return;
  }

  if (!dragging.value) return;

  dragging.value = false;

  if (axis === "x") {
    settle();
  } else {
    // Tap or vertical scroll: no spring will run, so open the warm window now.
    scheduleWarm();
  }

  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", onPointerUp);
};

const onTouchStart = (event: TouchEvent) => {
  const touch = event.touches[0];
  beginDrag(touch.clientX, touch.clientY);
};

const onTouchMove = (event: TouchEvent) => {
  const touch = event.touches[0];
  moveDrag(touch.clientX, touch.clientY, event);
};

const onMouseDown = (event: MouseEvent) => {
  // A pointer interaction should leave the gallery ready for its documented
  // keyboard controls. Do not prevent the native event here: that would keep
  // the video play button from receiving its normal focus and activation.
  galleryRoot.value?.focus({ preventScroll: true });
  beginDrag(event.clientX, event.clientY);
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", onPointerUp);
};

const onMouseMove = (event: MouseEvent) => {
  moveDrag(event.clientX, event.clientY);
};

const clampZoom = () => {
  zoomTx.value = clamp(zoomTx.value, -zoomMaxX, zoomMaxX);
  zoomTy.value = clamp(zoomTy.value, -zoomMaxY, zoomMaxY);
};

// Per-slide zoom transform; identity (no inline style) for non-zoomed slides.
// transform-origin is the media centre (set in CSS), so the focal translate below
// keeps the tapped point under the finger.
const mediaZoomStyle = (index: number) => {
  if (zoomedIndex.value !== index) return {};
  return {
    transform: `translate3d(${zoomTx.value}px, ${zoomTy.value}px, 0) scale(${ZOOM_SCALE})`,
  };
};

const resetZoom = () => {
  zoomedIndex.value = null;
  zoomTx.value = 0;
  zoomTy.value = 0;
  zoomPanning.value = false;
};

// Tap toggles zoom. A tap that followed a drag (didDrag) is ignored, so swipes
// and pans never trigger a zoom.
const onImageTap = (event: MouseEvent, index: number, item: GalleryItem) => {
  if (didDrag.value) return;

  toggleImageZoom(
    index,
    item,
    event.currentTarget as HTMLElement | null,
    event.clientX,
    event.clientY
  );
};

// Both pointer and keyboard zoom use the same image-box calculation. Keyboard
// activation supplies the centre of the real rendered image, which preserves
// the existing zoom behaviour without inventing a second transform path.
const toggleImageZoom = (
  index: number,
  item: GalleryItem,
  el: HTMLElement | null,
  clientX: number,
  clientY: number
) => {
  // Second tap on the already-zoomed photo -> reset.
  if (zoomedIndex.value === index) {
    resetZoom();
    return;
  }

  if (!el) return;
  const rect = el.getBoundingClientRect();
  const s = ZOOM_SCALE;
  const w = rect.width || 1;
  const h = rect.height || 1;
  const px = clientX - rect.left; // tap point within the media box
  const py = clientY - rect.top;

  // How far the scaled media may pan while still covering the frame.
  zoomMaxX = ((s - 1) * w) / 2;
  zoomMaxY = ((s - 1) * h) / 2;

  // Translate so the tapped point stays put when scaling about the centre.
  zoomTx.value = (1 - s) * (px - w / 2);
  zoomTy.value = (1 - s) * (py - h / 2);
  clampZoom();

  zoomedIndex.value = index;
  emit("image-click", { index, item });
};

// Arrow keys pressed on a control inside the gallery (the video play button)
// move to a slide that makes the control's own slide inert. Hand focus to the
// gallery region first: otherwise the browser drops focus to <body> and the
// next arrow key no longer reaches the gallery.
const goToFromKeyboard = (index: number) => {
  const target = clamp(index, 0, Math.max(0, props.items.length - 1));
  const root = galleryRoot.value;
  const focused = document.activeElement;
  if (
    target !== activeIndex.value &&
    root &&
    focused !== root &&
    root.contains(focused)
  ) {
    root.focus({ preventScroll: true });
  }
  goTo(target);
};

const onGalleryKeydown = (event: KeyboardEvent) => {
  if (event.defaultPrevented) return;

  const target = event.target as Element | null;
  // A focused <video> owns its keys (seek, volume, play/pause) whenever it
  // exposes native controls.
  if (target?.closest("video")) return;

  if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
    // Modified arrows belong to the browser, the OS and assistive tech: history
    // back / forward, word or line jumps, selection. Only plain arrows drive
    // the gallery.
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
      return;
    event.preventDefault();
    goToFromKeyboard(activeIndex.value + (event.key === "ArrowLeft" ? -1 : 1));
    return;
  }

  if (event.key === "Escape") {
    if (zoomedIndex.value === null) return;
    event.preventDefault();
    resetZoom();
    return;
  }

  if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") {
    return;
  }

  // A button keeps its native Enter / Space activation: the video play button
  // must play or pause the clip, never zoom a photo.
  if (target?.closest("button")) return;

  const index = activeIndex.value;
  const item = props.items[index];
  if (!item || isVideoItem(item)) return;

  const image = galleryRoot.value
    ?.querySelectorAll<HTMLElement>(".gallery-item")
    [index]?.querySelector<HTMLElement>(
      ".gallery-img:not(.gallery-video-poster):not(.gallery-media-preview):not(.gallery-transition-preview)"
    );
  if (!image) return;

  event.preventDefault();
  const rect = image.getBoundingClientRect();
  toggleImageZoom(
    index,
    item,
    image,
    rect.left + rect.width / 2,
    rect.top + rect.height / 2
  );
};

// Pan the zoomed media. Used in place of carousel swipe while zoomed.
const beginPan = (x: number, y: number) => {
  zoomPanning.value = true;
  didDrag.value = false;
  axis = null;
  panStartX = x;
  panStartY = y;
  zoomStartTx = zoomTx.value;
  zoomStartTy = zoomTy.value;
};

const movePan = (x: number, y: number, event?: TouchEvent) => {
  if (!zoomPanning.value) return;
  const dx = x - panStartX;
  const dy = y - panStartY;
  if (Math.abs(dx) > 4 || Math.abs(dy) > 4) didDrag.value = true;
  event?.preventDefault(); // keep the page from scrolling while panning
  zoomTx.value = zoomStartTx + dx;
  zoomTy.value = zoomStartTy + dy;
  clampZoom();
};

// Pause the looping product video whenever the gallery is scrolled out of
// view, resume the active slide's video when it returns. A muted looping
// clip kept playing behind the page while the user read the accordions
// below: every loop restart (~1s for short clips) forced a compositor
// commit that visibly nudged the ENTIRE page ±1px — measured frame-by-frame
// from a screen recording as rhythmic one-frame excursions at ~1Hz, and the
// reason only "some products" (the ones whose gallery has a video) ever
// wobbled. Pausing offscreen also saves decode work and battery.
let galleryVisibilityObserver: IntersectionObserver | null = null;
let galleryResizeObserver: ResizeObserver | null = null;
const isGalleryOnScreen = ref(true);

onMounted(() => {
  measure();
  window.addEventListener("resize", measure);
  if (typeof ResizeObserver !== "undefined" && track.value) {
    galleryResizeObserver = new ResizeObserver(([entry]) => {
      if (Math.abs(entry.contentRect.width - slideWidth) > 0.01) measure();
    });
    galleryResizeObserver.observe(track.value);
  }
  window.addEventListener("online", retryMedia);
  document.addEventListener("visibilitychange", onGalleryVisibilityChange);
  // SSR may finish a cached hero before Vue attaches its load listener.
  const hero = galleryRoot.value?.querySelector<HTMLImageElement>(
    ".gallery-item:first-child .gallery-img:not(.gallery-transition-preview):not(.gallery-media-preview)"
  );
  if (hero?.complete && hero.naturalWidth > 1) onMediaLoad(0);

  if (
    typeof window !== "undefined" &&
    "IntersectionObserver" in window &&
    galleryRoot.value
  ) {
    galleryVisibilityObserver = new IntersectionObserver(
      (entries) => {
        const visible = !!entries[0]?.isIntersecting;
        if (visible === isGalleryOnScreen.value) return;
        isGalleryOnScreen.value = visible;
        if (visible) {
          applyVideoPlaybackForActiveIndex();
          scheduleWarm();
        } else {
          pauseActiveVideo();
          cancelWarmSchedule();
          pruneWarmMedia(new Set());
        }
      },
      { threshold: 0.01 }
    );
    galleryVisibilityObserver.observe(galleryRoot.value);
  }
});

onBeforeUnmount(() => {
  warmGeneration += 1;
  window.removeEventListener("resize", measure);
  galleryResizeObserver?.disconnect();
  window.removeEventListener("online", retryMedia);
  document.removeEventListener("visibilitychange", onGalleryVisibilityChange);
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", onPointerUp);
  cancelAnimation();
  cancelWarmSchedule();
  galleryVisibilityObserver?.disconnect();
  galleryVisibilityObserver = null;
  if (transitionPreviewTimer) clearTimeout(transitionPreviewTimer);
});

// Treat items as the SAME product unless their structural identity changes, and
// key video items by their stable `videoUrl` rather than their poster. Posters
// are now static CDN URLs so they no longer churn, but a poster that falls back
// to the product photo still must not be read as "a different product" and yank
// the visitor back to slide 0 mid-browse.
const itemsSignature = (items: GalleryItem[]) =>
  items.length +
  ":" +
  items.map((it) => it?.videoUrl || it?.image || it?.src || "").join("|");

let lastItemsSignature = "";

watch(
  () => props.items,
  async (items) => {
    const list = items || [];
    const signature = itemsSignature(list);
    const isNewProduct = signature !== lastItemsSignature;
    const isFirstProduct = !lastItemsSignature;
    lastItemsSignature = signature;

    if (isNewProduct) {
      // Different product (or first load): start from the first slide, and drop
      // every video back to "not requested" so the new product's clips are as
      // untouched as the previous one's were before anybody pressed play.
      resetAllVideos();
      videoRefs.clear();
      resetZoom();
      cancelAnimation();
      dragging.value = false;
      cancelWarmSchedule();
      warmGeneration += 1;
      if (transitionPreviewTimer) clearTimeout(transitionPreviewTimer);
      transitionPreviewTimer = null;
      transitionPreviewRetiring.value = false;
      transitionPreviewVisible.value =
        Boolean(props.transitionPreviewSrc) && isFirstProduct;
      mediaError.value = "";
      heroReady = false;
      warmDirection = 1;
      loadedMediaIndices.clear();
      visitedMediaIndices.clear();
      previewMediaIndices.clear();
      previewPaintedMediaIndices.clear();
      paintedMediaIndices.clear();
      failedMediaIndices.clear();
      originalSourceIndices.clear();
      pendingMediaIndices.clear();
      for (const key of Object.keys(mediaRetries))
        delete mediaRetries[Number(key)];
      for (const key of Object.keys(posterOverrides)) {
        delete posterOverrides[key as unknown as number];
      }
      for (const key of Object.keys(previewOverrides))
        delete previewOverrides[Number(key)];
      activeIndex.value = 0;
      priorityMediaIndex.value = 0;
      translateX = 0;
      // The hero slide is the LCP element and must carry a real src in the SSR
      // HTML — it bypasses the warm-up gate entirely.
      if (list.length > 0) {
        loadedMediaIndices.add(0);
        visitedMediaIndices.add(0);
      }
      const generation = warmGeneration;
      await nextTick();
      if (generation !== warmGeneration) return;
      measure();
      const hero = galleryRoot.value?.querySelector<HTMLImageElement>(
        ".gallery-item:first-child .gallery-img:not(.gallery-transition-preview):not(.gallery-media-preview)"
      );
      if (hero?.complete && hero.naturalWidth > 1) onMediaLoad(0);
      await nextTick();
      applyVideoPlaybackForActiveIndex();
    } else {
      // Same product, items array merely rebuilt (e.g. the video poster resolved).
      // Keep the user exactly where they are; just refresh the load window.
      activeIndex.value = clamp(
        activeIndex.value,
        0,
        Math.max(0, list.length - 1)
      );
      if (heroReady) scheduleWarm();
    }
  },
  { immediate: true }
);

// Borrow only completed thumbnail downloads. They remain visible underneath
// an unfinished request, without downgrading an already-painted main photo.
watch(
  [() => props.thumbnailPreviews, () => props.items],
  () => {
    props.items.forEach((item, index) => {
      const key = item.videoUrl || item.image || item.src || "";
      const source = props.thumbnailPreviews[key];
      if (
        !source ||
        previewOverrides[index] === source ||
        previewPaintedMediaIndices.has(index) ||
        paintedMediaIndices.has(index)
      )
        return;
      previewOverrides[index] = source;
      previewMediaIndices.add(index);
    });
  },
  { deep: true, immediate: true }
);

// Re-prioritise any unfinished queue around the active slide. On normal
// connections compact galleries are usually already complete; long and
// constrained galleries advance their rolling window. The parent still
// receives thumbnail state.
watch(activeIndex, (idx) => {
  if (!canLoadMedia(idx)) prioritizeMedia(idx);
  scheduleWarm();
  emit("slide-change", { index: idx });
});

// Imperative navigation for external controls (e.g. desktop thumbnails).
const goTo = (index: number) => {
  const target = clamp(index, 0, Math.max(0, props.items.length - 1));
  if (zoomedIndex.value !== null) resetZoom();
  mediaError.value = "";
  prioritizeMedia(target);
  animateSpringTo(target, 0);
};

defineExpose({ goTo });
</script>
